import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Trees,
  Leaf,
  Droplets,
  Bird,
  Briefcase,
  Sprout,
  Users,
  ArrowRight,
  Bug,
  CircleDot,
  Layers,
  Microscope,
} from "lucide-react";
import Navigation from "@/components/landing/Navigation";
import Footer from "@/components/landing/Footer";

const principles = [
  {
    icon: Trees,
    title: "Shade-first design",
    lead:
      "In hot remote communities, shade is infrastructure.",
    body: [
      "You are not just growing food — you are reducing heat load, evapotranspiration, dust, wind and soil death. A badly designed orchard becomes an oven.",
      "Windbreaks. Nurse trees. Multi-layer canopy. Shaded understory production. Water-harvesting contours and swales. Protected microclimates. The orchard begins with the air and the ground long before the fruit.",
    ],
    image:
      "https://customer-assets.emergentagent.com/job_country-gardens/artifacts/i9w8afat_orchard%201.jpg",
    imageAlt:
      "Mature citrus orchard with dappled shade on red-earth Country — canopy, mulch and microclimate at work",
  },
  {
    icon: Leaf,
    title: "Hardy perennial systems",
    lead:
      "Annual vegetables are labour-heavy, water-heavy and management-heavy. Perennials are where remote resilience lives.",
    body: [
      "A remote orchard should function like a semi-wild productive ecosystem, not a suburban market garden.",
      "Citrus, mulberry, pomegranate, fig, moringa, native plums, bush foods, olives, carob, desert lime — and in suitable zones, date palms, tamarind, banana and cassava. Layered species, not monocultures.",
    ],
    image:
      "https://customer-assets.emergentagent.com/job_country-gardens/artifacts/nddl1lkl_orchard%204.jpeg",
    imageAlt:
      "Ripe oranges on the tree in golden light — productive perennial citrus, the resilience proof",
  },
  {
    icon: Droplets,
    title: "Water harvesting over irrigation",
    lead:
      "Most failed remote food projects depend entirely on imported infrastructure — pumps, drip systems, fuel, replacement parts, technicians. That is fragile.",
    body: [
      "The goal is to build a sponge, not a hydroponic dependency.",
      "Contour planting. Infiltration basins. Mulch systems. Greywater reuse. Roof water harvesting. Passive hydration. Deep organic matter. Biochar. Dense root systems that hold moisture in the soil itself.",
    ],
  },
  {
    icon: Bird,
    title: "Integrate animals",
    lead:
      "There is no healthy ecosystem without animals. Remote food systems should reflect that.",
    body: [
      "Chickens, ducks, controlled small-goat systems where appropriate, worm farms and compost systems.",
      "Animals cycle nutrients, reduce weeds, create fertility, increase microbial life and provide protein. That is real food security — not chemicals, not imports.",
    ],
  },
  {
    icon: Briefcase,
    title: "Community-scale enterprise",
    lead:
      "If an orchard is treated as 'a project', it dies. If it creates capability, it lives.",
    body: [
      "Jobs. Products. Medicine. Shade. Nursery capability. Seed systems. Training. Tourism. Youth engagement. Cultural authority.",
      "This is the line between charity gardening and ecological infrastructure. The orchards that survive are the ones embedded in real community economy.",
    ],
  },
  {
    icon: Sprout,
    title: "Native + introduced species together",
    lead:
      "Purely imported food systems disconnect culture. Purely native systems struggle to meet nutrition demand at scale. The honest model is hybrid.",
    body: [
      "Bush foods. Medicinal plants. Productive introduced species. Agroforestry overstory. Shade species. Pollinator systems.",
      "Integrated together — with local knowledge guiding which species sit where, and why.",
    ],
    image:
      "https://customer-assets.emergentagent.com/job_country-gardens/artifacts/9z1otywe_Wattle%20orchard%202.png",
    imageAlt:
      "Mature native wattle (acacia) shelter-belt forming a green corridor on Country — native species working as windbreak, nurse plants and pollinator habitat alongside productive food systems",
  },
  {
    icon: Users,
    title: "Orchards as social infrastructure",
    lead:
      "This is the deeper layer most policy conversations miss.",
    body: [
      "Shade creates gathering. Gathering creates participation. Participation creates ownership. Ownership creates maintenance.",
      "A productive shaded landscape changes community behaviour — for Elders, young people, women's groups, ranger programs and school engagement. You are not just growing fruit. You are rebuilding relationship with place.",
    ],
  },
];

const failureModel = [
  "Externally designed",
  "Input dependent",
  "Externally maintained",
  "Short funded",
  "Socially disconnected",
  "Disconnected from place",
];

const livingModel = [
  "Beautiful",
  "Culturally owned",
  "Low-input",
  "Productive",
  "Shaded",
  "Resilient",
  "Economically useful",
  "Ecologically regenerative",
];

export default function Orchards() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <main data-testid="orchards-page" className="bg-sand text-charcoal">
      <Navigation scrolled={scrolled} />

      {/* COVER */}
      <section
        data-testid="orchards-cover"
        className="relative min-h-[80vh] flex items-center bg-charcoal text-sand overflow-hidden"
      >
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_country-gardens/artifacts/sbxzufv9_orchard%202.jpg"
            alt="Aerial of an established orchard in remote red-earth Country — productive rows surrounded by sparse arid landscape"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-charcoal/75 via-charcoal/65 to-charcoal" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-32 md:py-40 w-full">
          <Link
            to="/"
            data-testid="orchards-back-link"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-sand/60 hover:text-ochre transition-colors mb-12"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
            Back to Growing Country
          </Link>

          <div className="text-xs md:text-sm uppercase tracking-[0.32em] text-ochre mb-6 flex items-center gap-4">
            <span className="inline-block w-10 h-px bg-ochre" />
            Living Infrastructure
          </div>

          <h1
            data-testid="orchards-title"
            className="font-serif font-light text-5xl sm:text-6xl md:text-7xl lg:text-[88px] leading-[0.95] tracking-tight max-w-5xl"
          >
            Orchards as
            <span className="block italic text-ochre">
              ecological infrastructure.
            </span>
          </h1>

          <p className="mt-10 max-w-2xl text-base md:text-lg leading-relaxed text-sand/80">
            Orchards are not a side idea for remote food security. They are one
            of the few systems that can create multi-decade food resilience in
            communities — if they are designed properly.
          </p>
        </div>
      </section>

      {/* INTRO */}
      <section className="relative bg-sand py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0 grain-overlay" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
            <div className="md:col-span-5">
              <div className="text-xs uppercase tracking-[0.3em] text-rust mb-6">
                Reframing the Word
              </div>
              <h2 className="font-serif text-4xl md:text-5xl font-light leading-[1.05] tracking-tight">
                Beyond rows of
                <span className="block italic text-rust">fruit trees.</span>
              </h2>

              <div className="mt-12 relative">
                <img
                  src="https://customer-assets.emergentagent.com/job_country-gardens/artifacts/hx5wogwi_orchard%20from%20air.jpg"
                  alt="Aerial of an established orchard on red-earth Country, set against the wider arid landscape and homestead"
                  className="w-full aspect-[4/3] object-cover"
                />
                <div className="absolute -bottom-5 -right-5 hidden md:block bg-rust text-sand px-5 py-3 text-[11px] uppercase tracking-[0.25em]">
                  Kenmore Park Food Orchard
                </div>
              </div>
            </div>
            <div className="md:col-span-6 md:col-start-7 space-y-6 text-base md:text-lg leading-relaxed text-ink">
              <p>
                The mistake most people make is thinking "orchard" means rows
                of fruit trees. That is industrial horticulture thinking.
              </p>
              <p>
                In remote Australia — and especially in arid and semi-arid
                Indigenous contexts — an orchard should be understood as a{" "}
                <em>layered food and water system</em> woven into community
                infrastructure, shade, soil repair, nutrition, cultural
                practice and local enterprise.
              </p>
              <p className="font-serif text-2xl md:text-3xl text-charcoal italic leading-snug border-l-2 border-rust pl-6 my-10">
                Resilience is built through soil biology, water infiltration,
                shelter and diversity — not inputs.
              </p>
              <p>
                Integrated systems outperform isolated crops. Living
                groundcover and microbial health are foundational. Tree
                systems dramatically improve landscape function over time —
                surviving drought, fungal pressure, extreme wind, frost and
                rainfall variability when designed well.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 7 PRINCIPLES */}
      <section className="relative bg-stone-alt py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-16 md:mb-20">
            <div className="md:col-span-7">
              <div className="text-xs uppercase tracking-[0.3em] text-rust mb-6">
                Seven Design Principles
              </div>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight">
                A serious remote
                <span className="block italic text-sage">orchard model.</span>
              </h2>
            </div>
            <div className="md:col-span-5">
              <p className="text-base md:text-lg leading-relaxed text-ink">
                Seven principles drawn from systems thinking — not from
                industrial agriculture — and tested against the realities of
                drought, distance and disruption.
              </p>
            </div>
          </div>

          <div className="space-y-px bg-stone-line border border-stone-line">
            {principles.map((p, i) => {
              const Icon = p.icon;
              return (
                <article
                  key={p.title}
                  data-testid={`orchard-principle-${i}`}
                  className="bg-sand p-8 md:p-12 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-start"
                >
                  <div className="md:col-span-4 flex items-start gap-6">
                    <span className="font-serif text-5xl md:text-6xl text-rust/80 tick-num leading-none shrink-0">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div>
                      <Icon
                        strokeWidth={1.5}
                        className="w-7 h-7 text-rust mb-4"
                      />
                      <h3 className="font-serif text-2xl md:text-3xl leading-tight text-charcoal">
                        {p.title}
                      </h3>
                    </div>
                  </div>
                  <div className="md:col-span-8 space-y-5">
                    {p.image && (
                      <img
                        src={p.image}
                        alt={p.imageAlt}
                        className="w-full aspect-[16/9] object-cover mb-2"
                      />
                    )}
                    <p className="font-serif text-xl md:text-2xl italic text-charcoal leading-snug">
                      {p.lead}
                    </p>
                    {p.body.map((line, bi) => (
                      <p
                        key={bi}
                        className="text-base md:text-lg leading-relaxed text-ink"
                      >
                        {line}
                      </p>
                    ))}
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* THE HARD TRUTH — softened */}
      <section className="relative bg-charcoal text-sand py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0 grain-overlay opacity-40" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-start">
            <div className="md:col-span-5 md:sticky md:top-28">
              <div className="text-[11px] uppercase tracking-[0.3em] text-ochre mb-6">
                The Honest Pattern
              </div>
              <h2 className="font-serif text-4xl md:text-5xl font-light leading-[1.05] tracking-tight">
                Why so many projects
                <span className="block italic text-ochre">fall short.</span>
              </h2>
              <p className="mt-8 text-base md:text-lg leading-relaxed text-sand/80 max-w-md">
                Decades of remote food projects have struggled to deliver
                lasting outcomes — not because communities lacked will, but
                because of how the systems themselves were designed.
              </p>
            </div>

            <div className="md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-10">
              <div>
                <div className="text-[11px] uppercase tracking-[0.28em] text-sand/55 mb-5">
                  Where they fall short
                </div>
                <ul className="space-y-3">
                  {failureModel.map((f, i) => (
                    <li
                      key={f}
                      data-testid={`failure-${i}`}
                      className="font-serif text-xl text-sand/85 border-b border-sand/15 pb-3"
                    >
                      {f}
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <div className="text-[11px] uppercase tracking-[0.28em] text-ochre mb-5">
                  What lasting systems look like
                </div>
                <ul className="space-y-3">
                  {livingModel.map((l, i) => (
                    <li
                      key={l}
                      data-testid={`living-${i}`}
                      className="font-serif text-xl text-sand border-b border-ochre/30 pb-3"
                    >
                      {l}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HEALTHY SOILS — SYNTHESIS */}
      <section
        id="soils"
        data-testid="healthy-soils-section"
        className="relative bg-sand py-24 md:py-36 overflow-hidden"
      >
        <div className="absolute inset-0 grain-overlay" />

        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-start mb-16 md:mb-20">
            <div className="md:col-span-5 md:sticky md:top-28">
              <div className="text-xs uppercase tracking-[0.3em] text-sage mb-6">
                Living Infrastructure
              </div>
              <h2
                data-testid="soils-heading"
                className="font-serif text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight text-charcoal"
              >
                Healthy soils,
                <span className="block italic text-sage">
                  healthy communities.
                </span>
              </h2>
            </div>

            <div className="md:col-span-6 md:col-start-7 space-y-6 text-base md:text-lg leading-relaxed text-ink">
              <p>
                Growing Country recognises that food security begins beneath
                our feet. Healthy soils contain complex communities of
                beneficial fungi, microbes, insects and other organisms that
                help plants access nutrients, resist disease and withstand
                environmental stress.
              </p>
              <p>
                By combining regenerative orchard systems, food forests,
                composting, water harvesting and biological soil management,
                communities can rebuild living soil systems that support
                long-term productivity while reducing reliance on external
                inputs.
              </p>
              <p className="font-serif text-2xl md:text-3xl text-charcoal italic leading-snug border-l-2 border-sage pl-6 my-10">
                Healthy soils are not simply a growing medium.
                <span className="block mt-2 not-italic text-sage">
                  They are living infrastructure that supports food production,
                  biodiversity, climate resilience and community wellbeing.
                </span>
              </p>
              <p className="text-charcoal font-medium">
                Healthy ecosystems naturally suppress disease — native soils
                often contain far greater populations of beneficial fungi than
                heavily managed agricultural soils, capable of suppressing
                plant pathogens without external intervention.
              </p>
            </div>
          </div>

          {/* Six-part framework */}
          <div className="border-t border-stone-line/70 pt-16">
            <div className="text-xs uppercase tracking-[0.3em] text-rust mb-3">
              A Complete Framework
            </div>
            <h3 className="font-serif text-3xl md:text-4xl font-light leading-tight text-charcoal max-w-3xl">
              Not just a garden project.
              <span className="block italic text-rust">
                The foundations of a community-scale ecological food system.
              </span>
            </h3>

            <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-stone-line border border-stone-line">
              {[
                {
                  icon: Droplets,
                  num: "01",
                  title: "Water security",
                  body: "Harvesting, infiltration and storage that build a sponge in the landscape.",
                },
                {
                  icon: Microscope,
                  num: "02",
                  title: "Soil biology",
                  body: "Fungi, microbes and organic matter — the living foundation beneath every productive system.",
                },
                {
                  icon: Bug,
                  num: "03",
                  title: "Beneficial insects",
                  body: "Pollinators, predators and decomposers cycling nutrients and balancing pressure.",
                },
                {
                  icon: Layers,
                  num: "04",
                  title: "Food forests & perennials",
                  body: "Layered, multi-decade productive systems that work with climate, not against it.",
                },
                {
                  icon: CircleDot,
                  num: "05",
                  title: "Seed & propagation systems",
                  body: "Locally-saved seed, nurseries and bush-food propagation that hold knowledge on Country.",
                },
                {
                  icon: Users,
                  num: "06",
                  title: "Community ownership & enterprise",
                  body: "Long-term capability, cultural authority and economic participation built into the system.",
                },
              ].map((f, i) => {
                const Icon = f.icon;
                return (
                  <div
                    key={f.num}
                    data-testid={`framework-${i}`}
                    className="bg-sand p-7 md:p-8 group hover:bg-charcoal hover:text-sand transition-colors duration-500"
                  >
                    <div className="flex items-center justify-between mb-6">
                      <Icon
                        className="w-6 h-6 text-sage group-hover:text-ochre transition-colors"
                        strokeWidth={1.5}
                      />
                      <span className="font-serif text-2xl text-ink/40 group-hover:text-sand/40 tick-num leading-none transition-colors">
                        {f.num}
                      </span>
                    </div>
                    <h4 className="font-serif text-xl md:text-2xl leading-tight">
                      {f.title}
                    </h4>
                    <p className="mt-3 text-sm leading-relaxed text-ink group-hover:text-sand/75 transition-colors">
                      {f.body}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* CLOSING MANIFESTO */}
      <section className="relative bg-charcoal text-sand py-24 md:py-36 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_country-gardens/artifacts/sbxzufv9_orchard%202.jpg"
            alt="Aerial of established orchard rows in remote red-earth Country"
            className="w-full h-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-charcoal/80 via-charcoal/85 to-charcoal" />
          <div className="absolute inset-0 grain-overlay opacity-30" />
        </div>
        <div className="relative max-w-5xl mx-auto px-6 md:px-12 text-center">
          <div
            aria-hidden
            className="font-serif text-ochre text-7xl md:text-8xl leading-none mb-4 select-none"
          >
            "
          </div>
          <blockquote
            data-testid="orchards-closing-quote"
            className="font-serif text-3xl md:text-4xl lg:text-5xl font-light leading-[1.15] tracking-tight text-sand"
          >
            This is not gardening.
            <span className="block mt-5 italic text-ochre">
              It is living ecological infrastructure for community resilience,
              cultural continuity, nutrition and enterprise development.
            </span>
          </blockquote>

          <div className="mt-14 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/#contact"
              data-testid="orchards-cta-contact"
              className="inline-flex items-center gap-3 bg-rust text-sand px-8 py-4 text-xs uppercase tracking-[0.25em] hover:bg-rust-dark transition-colors"
            >
              Discuss an orchard system
              <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
            <Link
              to="/policy-briefing"
              data-testid="orchards-cta-briefing"
              className="inline-flex items-center gap-3 border border-sand/60 text-sand px-8 py-4 text-xs uppercase tracking-[0.25em] hover:bg-sand hover:text-charcoal transition-colors"
            >
              Read the policy briefing
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
